import type { FeatureCollection, MultiPolygon, Polygon, Position } from "geojson";
import { buildingHeightMeters, type BuildingProperties } from "../lib/building";

export interface RoofEvidence {
  source: string;
  acquired: string;
  imageryDate: string;
  roofs: Record<string, { shape: "flat" | "gabled"; color: [number, number, number]; confidence: number; method: string }>;
}
export interface RoofFace {
  buildingId: string;
  polygon: Position[] | Position[][];
  color: [number, number, number];
}

/** Clip a convex footprint to either side of its ridge, keeping real edges. */
function half(ring: Position[], signed: (p: Position) => number, side: number): Position[] {
  const out: Position[] = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    const da = signed(a) * side, db = signed(b) * side;
    if (da >= -1e-12) out.push(a);
    if ((da > 0 && db < 0) || (da < 0 && db > 0)) {
      const t = da / (da - db);
      out.push([a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]);
    }
  }
  return out;
}

function convex(ring: Position[]): boolean {
  let sign = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length], c = ring[(i + 2) % ring.length];
    const cross = (b[0] - a[0]) * (c[1] - b[1]) - (b[1] - a[1]) * (c[0] - b[0]);
    if (Math.abs(cross) < 1e-15) continue;
    if (sign && Math.sign(cross) !== sign) return false;
    sign = Math.sign(cross);
  }
  return sign !== 0;
}

/** All footprints get roofs. Complex/courtyard footprints retain flat roofs;
 * a simple ridge estimate is never stretched across a concavity or a hole.
 * Heights are illustrative source heights, with no vertical exaggeration.
 */
export function makeRoofFaces(
  collection: FeatureCollection<Polygon | MultiPolygon, BuildingProperties>,
  evidence: RoofEvidence | null,
): RoofFace[] {
  const faces: RoofFace[] = [];
  for (const feature of collection.features) {
    const p = feature.properties;
    const model = evidence?.roofs[p.id];
    const color: [number, number, number] = model?.color ?? [153, 151, 144];
    const eaves = Number(p.height) > 0 ? Number(p.height) : buildingHeightMeters(p);
    const parts = feature.geometry.type === "Polygon" ? [feature.geometry.coordinates] : feature.geometry.coordinates;
    for (const rings of parts) {
      const outer = rings[0];
      if (!outer || outer.length < 4) continue;
      const ring = outer.slice(0, -1);
      if (model?.shape !== "gabled" || rings.length > 1 || !convex(ring)) {
        faces.push({ buildingId: p.id, color, polygon: rings.map(r => r.map(v => [v[0], v[1], eaves + 0.03])) });
        continue;
      }
      // The longest footprint edge sets ridge bearing; no invented north-south roofs.
      const cos = Math.cos(ring[0][1] * Math.PI / 180);
      let ux = 1, uy = 0, longest = 0;
      for (let i = 0; i < ring.length; i++) {
        const a = ring[i], b = ring[(i + 1) % ring.length];
        const x = (b[0] - a[0]) * cos, y = b[1] - a[1], len = Math.hypot(x, y);
        if (len > longest) { longest = len; ux = x / len; uy = y / len; }
      }
      const projection = (v: Position) => -uy * v[0] * cos + ux * v[1];
      const values = ring.map(projection), lo = Math.min(...values), hi = Math.max(...values);
      const width = (hi - lo) * 111_320;
      if (width < 1) {
        faces.push({ buildingId: p.id, color, polygon: ring.map(v => [v[0], v[1], eaves + 0.03]) });
        continue;
      }
      const mid = (lo + hi) / 2, rise = Math.min(3, width * 0.22);
      const signed = (v: Position) => projection(v) - mid;
      const atRoof = (v: Position): Position => [v[0], v[1], eaves + rise * (1 - Math.min(1, Math.abs(signed(v)) / ((hi - lo) / 2)))];
      for (const side of [-1, 1]) {
        const plane = half(ring, signed, side);
        if (plane.length < 3) continue;
        faces.push({ buildingId: p.id, color: color.map(c => Math.min(255, Math.round(c * (side < 0 ? 0.88 : 1.08)))) as [number, number, number], polygon: plane.map(atRoof) });
        // Close gable ends above the wall; the ridge is a surface, not a floating lid.
        for (let i = 0; i < plane.length; i++) {
          const a = plane[i], b = plane[(i + 1) % plane.length];
          if (Math.abs(signed(a)) < 1e-12 && Math.abs(signed(b)) < 1e-12) continue;
          faces.push({ buildingId: p.id, color, polygon: [[a[0], a[1], eaves], [b[0], b[1], eaves], atRoof(b), atRoof(a)] });
        }
      }
    }
  }
  return faces;
}
